// App shell and single state owner for the active UI tree.
//
// Renders LoginScreen (unauthed) or the screen router (authed). Owns: trips
// (optimistic local copies of server state), currentTripId, the new-trip
// wizard draft, selectedIdeas, and screen navigation. All persistence flows
// through utils/api.js (server-side admin SDK); local state is updated
// optimistically first, then PATCHed.
//
// Exports ideasForTrip / reconcileSelectedIdeas for tests. Note the app has a
// second, LEGACY component tree (components/{home,log,settings,wizard,trip} +
// contexts/TripContext) that is NOT rendered — don't wire it back in.
import React from 'react';
import { Sidebar } from './components/Sidebar';
import { HomeScreen } from './screens/HomeScreen';
import { NewTripScreen, PlanningPathScreen, addDaysIso } from './screens/NewTripScreen';
import { computeLegSchedule } from './utils/legSchedule';
import { PresetsScreen } from './screens/PresetsScreen';
import { BrainstormScreen } from './screens/BrainstormScreen';
import { ItineraryScreen } from './screens/ItineraryScreen';
import { BookingScreen } from './screens/BookingScreen';
import { SharedItineraryView } from './screens/SharedItineraryView';
import { ShareInboxScreen } from './screens/ShareInboxScreen';
import { loadTrips, destSwatch } from './utils/trips';
import { mergeScannedTripDetails } from './utils/scanDetails';
import { apiCreateTrip, apiUpdateTrip, apiDeleteTrip, apiFetchTrips, getToken } from './utils/api';
import { useAuth } from './contexts/AuthContext';
import { sendPasswordReset } from './utils/firebase';
import { PALETTE } from './theme';
import { getIdeasForDest, getIdeasForLegs } from './data';
import { APP_VERSION } from './version';

// Bottom-right build stamp — bump APP_VERSION in src/version.js on every
// deploy so it's obvious whether the browser is showing a stale cached copy.
function VersionBadge() {
  const isStaging = process.env.PUBLIC_URL === '/test';
  return (
    <div style={{
      position: 'fixed', bottom: 8, right: 10, zIndex: 999,
      fontFamily: 'JetBrains Mono, monospace', fontSize: 9, letterSpacing: 0.5,
      color: PALETTE.inkMute, opacity: 0.55, pointerEvents: 'none', userSelect: 'none',
    }}>
      {APP_VERSION}{isStaging ? ' · staging' : ''}
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function useIsWide() {
  const [isWide, setIsWide] = React.useState(() => window.innerWidth >= 900);
  React.useEffect(() => {
    const handler = () => setIsWide(window.innerWidth >= 900);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isWide;
}

// ── Login screen (inline-style, matches app palette) ─────────────────────────
function LoginScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = React.useState('login');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handle = async () => {
    if (!email || !password) { setError('Enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password);
    } catch (e) {
      const c = e.code || '';
      if (mode === 'login') {
        if (c.includes('user-not-found') || c.includes('invalid-credential') || c.includes('invalid-email') || c.includes('wrong-password')) {
          setError('Email or password is incorrect.');
        } else if (c.includes('too-many-requests')) {
          setError('Too many failed attempts. Try again later.');
        } else {
          setError('Something went wrong. Try again.');
        }
      } else {
        if (c.includes('email-already-in-use')) {
          setError('An account with this email already exists. Sign in instead.');
        } else if (c.includes('weak-password')) {
          setError('Password must be at least 6 characters.');
        } else if (c.includes('invalid-email')) {
          setError('Please enter a valid email address.');
        } else {
          setError('Something went wrong. Try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const [resetSent, setResetSent] = React.useState(false);
  const [resetLoading, setResetLoading] = React.useState(false);
  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address first.'); return; }
    setResetLoading(true);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
      setError('');
    } catch (e) {
      setError('Could not send reset email. Check the address and try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const [focusedField, setFocusedField] = React.useState(null);
  const isWide = useIsWide();

  const inpStyle = (field) => ({
    appearance: 'none', border: `1px solid ${focusedField === field ? PALETTE.accent : PALETTE.rule}`,
    borderRadius: 10, padding: '13px 14px', fontFamily: 'Inter', fontSize: 15, color: PALETTE.ink,
    background: '#fff', width: '100%', boxSizing: 'border-box', outline: 'none',
    boxShadow: focusedField === field ? `0 0 0 3px rgba(184,82,48,0.15)` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  });

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex' }}>
      {/* Left panel: branding — only on wide screens */}
      {isWide && <div style={{
        flex: 1, background: PALETTE.paperDim, borderRight: `1px solid ${PALETTE.rule}`,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '48px 52px',
      }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 42, fontWeight: 400, letterSpacing: -1, color: PALETTE.ink, lineHeight: 1.1, marginBottom: 16 }}>
          Your trips,<br />beautifully planned.
        </div>
        <div style={{ fontFamily: 'Inter', fontSize: 15, color: PALETTE.inkSoft, lineHeight: 1.7, maxWidth: 340 }}>
          Build itineraries, scan booking confirmations, save ideas from Instagram, and keep all your travel details in one place.
        </div>
        <div style={{ marginTop: 40, display: 'flex', gap: 24 }}>
          {['Flights', 'Hotels', 'Itineraries'].map(label => (
            <div key={label} style={{
              fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute,
              letterSpacing: 1.2, textTransform: 'uppercase',
              padding: '4px 10px', border: `1px solid ${PALETTE.rule}`, borderRadius: 4,
            }}>
              {label}
            </div>
          ))}
        </div>
      </div>}

      {/* Right panel: form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 34, fontWeight: 400, letterSpacing: -0.8, color: PALETTE.ink, marginBottom: 6, lineHeight: 1.1 }}>
            Travel Planner
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkMute, marginBottom: 32, lineHeight: 1.5 }}>
            {mode === 'login' ? 'Welcome back. Sign in to continue.' : 'Create your account to get started.'}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {[['login', 'Sign in'], ['register', 'Create account']].map(([m, l]) => (
              <button key={m} onClick={() => { setMode(m); setError(''); }} style={{
                flex: 1, appearance: 'none', cursor: 'pointer', padding: '9px 0', borderRadius: 999,
                background: mode === m ? PALETTE.ink : 'transparent',
                color: mode === m ? PALETTE.paper : PALETTE.inkSoft,
                fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
                border: mode === m ? '1px solid transparent' : `1px solid ${PALETTE.rule}`,
                transition: 'all 0.15s',
              }}>{l}</button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: PALETTE.inkSoft, marginBottom: 6, letterSpacing: 0.2 }}>Email</label>
              <input
                style={inpStyle('email')} type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                onKeyDown={e => e.key === 'Enter' && handle()}
              />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <label style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: PALETTE.inkSoft, letterSpacing: 0.2 }}>Password</label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'Inter', fontSize: 12, color: PALETTE.accent, textDecoration: 'underline' }}
                  >
                    {resetLoading ? 'Sending…' : 'Forgot password?'}
                  </button>
                )}
              </div>
              <input
                style={inpStyle('password')} type="password" placeholder="••••••••" value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                onKeyDown={e => e.key === 'Enter' && handle()}
              />
            </div>
          </div>

          {resetSent && (
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#2e7d32', marginTop: 12, lineHeight: 1.4, padding: '10px 12px', background: '#e8f5e9', borderRadius: 8 }}>
              Reset email sent to {email}. Check your inbox.
            </div>
          )}
          {error && (
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.red, marginTop: 12, lineHeight: 1.4, padding: '10px 12px', background: PALETTE.redSoft, borderRadius: 8 }}>{error}</div>
          )}

          <button
            onClick={handle}
            disabled={loading}
            style={{
              width: '100%', marginTop: 20, appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer',
              background: loading ? PALETTE.inkMute : PALETTE.accent, color: '#fff',
              padding: '15px 0', borderRadius: 12, fontFamily: 'Inter', fontSize: 15, fontWeight: 600,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = PALETTE.accentDk; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = PALETTE.accent; }}
          >
            {loading ? 'Loading…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </div>
      </div>
      <VersionBadge />
    </div>
  );
}

export function ideasForTrip(trip) {
  const legs = trip?.legs && trip.legs.length > 1 ? trip.legs : null;
  const destinationIdeas = legs ? getIdeasForLegs(legs) : getIdeasForDest(trip?.destination);
  const rawCustomIdeas = trip?.customIdeas;
  const customIdeas = Array.isArray(rawCustomIdeas) ? rawCustomIdeas : Object.values(rawCustomIdeas || {});
  // Custom ideas have no natural city. If one was explicitly tagged to a leg
  // when added, re-derive destCity from the current legs (so a renamed city
  // stays in sync); otherwise leave it untagged (no legIndex/destCity) —
  // never guess a city for it. Untagged ideas show up in their own group
  // (IdeasPanel's cityGroups) and stay in the itinerary's parking lot
  // instead of silently packing into leg 0's days.
  const taggedCustom = legs
    ? customIdeas.map(i => i.legIndex != null ? { ...i, destCity: legs[i.legIndex]?.destination?.split(',')[0].trim() } : i)
    : customIdeas;
  return [...taggedCustom, ...destinationIdeas];
}

export function reconcileSelectedIdeas(savedIds, availableIdeas) {
  const fallbackIds = availableIdeas.map(i => i.id).filter(id => !String(id).startsWith('custom_'));
  if (!Array.isArray(savedIds)) return fallbackIds;
  const validIds = new Set(availableIdeas.map(i => i.id));
  const kept = savedIds.filter(id => validIds.has(id));
  // A non-empty saved list where nothing survives means the ids are stale
  // (e.g. generic ge* ids from before destination-specific ideas) — reset
  // to the destination's ideas rather than showing an empty trip.
  if (savedIds.length > 0 && kept.length === 0) return fallbackIds;
  return kept;
}

// ── Main app ──────────────────────────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const { user, loading: authLoading, logout } = useAuth();

  // Trips are fetched from the server (admin SDK) to bypass Firestore client rules
  const [trips, setTrips] = React.useState([]);
  const [tripsLoading, setTripsLoading] = React.useState(true);

  const refreshTrips = React.useCallback(async () => {
    if (!user) { setTrips([]); setTripsLoading(false); return; }
    try {
      const fetched = await apiFetchTrips(user);
      setTrips(fetched);
    } catch (e) {
      console.error('refreshTrips error:', e);
    } finally {
      setTripsLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    if (!user) { setTrips([]); setTripsLoading(false); return; }
    setTripsLoading(true);
    refreshTrips();
  }, [user, refreshTrips]);

  const [screen, setScreen] = React.useState('home');
  const [homeTab, setHomeTab] = React.useState('home');
  const [currentTripId, setCurrentTripId] = React.useState(null);

  // New trip wizard state
  // tripDraft.legs holds ADDITIONAL cities beyond the primary `destination`
  // (multi-city trips only); tripDraft.primaryDays is how many of the trip's
  // days the primary destination keeps once extra cities are added. See
  // fullLegs below and docs/state-model.md.
  const [tripDraft, setTripDraft] = React.useState({
    destination: '', dateStart: '', dateEnd: '', travelers: 2, legs: [], primaryDays: undefined,
  });
  const [selectedIdeas, setSelectedIdeas] = React.useState([]);
  const [brainstormCustomIdeas, setBrainstormCustomIdeas] = React.useState([]);
  const [dayCount, setDayCount] = React.useState(5);
  const [selectedPresets, setSelectedPresets] = React.useState([]);

  // Full ordered leg list (primary + extras) when this is a multi-city trip,
  // else null — meaning "treat as a single destination", unchanged from
  // pre-multi-city behavior.
  const fullLegs = React.useMemo(() => {
    const extra = (tripDraft.legs || []).filter(l => l.destination?.trim());
    if (extra.length === 0) return null;
    const extraSum = extra.reduce((s, l) => s + (l.dayCount || 0), 0);
    const primaryDays = tripDraft.primaryDays ?? Math.max(1, dayCount - extraSum);
    return [
      { destination: tripDraft.destination, dayCount: primaryDays },
      ...extra.map(({ destination, dayCount: dc, overlapStart }) => ({ destination, dayCount: dc, overlapStart: !!overlapStart })),
    ];
  }, [tripDraft.destination, tripDraft.legs, tripDraft.primaryDays, dayCount]);

  const destIdeas = React.useMemo(
    () => fullLegs ? getIdeasForLegs(fullLegs) : getIdeasForDest(tripDraft.destination),
    [tripDraft.destination, fullLegs]
  );

  // Auto-compute dayCount from date range whenever dates change. Multi-city
  // trips instead derive total dayCount from the sum of leg day counts.
  React.useEffect(() => {
    if (fullLegs) { setDayCount(computeLegSchedule(fullLegs).totalDays); return; }
    if (tripDraft.dateStart && tripDraft.dateEnd) {
      const d1 = new Date(tripDraft.dateStart + 'T12:00:00');
      const d2 = new Date(tripDraft.dateEnd + 'T12:00:00');
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
      if (diff > 0 && diff <= 60) setDayCount(diff);
    }
  }, [tripDraft.dateStart, tripDraft.dateEnd, fullLegs]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time migration: push any existing localStorage trips to server on first login
  React.useEffect(() => {
    if (!user || tripsLoading) return;
    const key = `tp_migrated_${user.uid}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    if (trips.length > 0) return; // server already has trips
    const local = loadTrips();
    if (local.length === 0) return;
    Promise.all(local.map(({ id: _id, createdAt: _ca, ...rest }) => apiCreateTrip(user, rest)))
      .then(() => refreshTrips())
      .catch(console.error);
  }, [user, tripsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Android "Share to Travel Planner" hand-off. The share_target POST lands
  // on the server (server/index.js can't attach a Bearer token to that OS-
  // initiated navigation), which stashes the payload and redirects here with
  // ?shareTarget=<token>. Redeem it once, with our own auth, then drop into
  // the ShareInboxScreen with whatever was shared.
  //
  // Deliberately NOT named `share` — that param is already the public
  // trip-sharing link token (see shareParam/SharedItineraryView below), which
  // is checked unconditionally before auth even loads. Reusing the name would
  // make an incoming share-target redirect render the public guest view
  // instead of ever reaching this effect.
  const [shareInboxPayload, setShareInboxPayload] = React.useState(null);
  React.useEffect(() => {
    if (!user) return;
    const token = new URLSearchParams(window.location.search).get('shareTarget');
    if (!token) return;
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      try {
        const idToken = await getToken(user);
        const r = await fetch(`/api/tp/share-target/${token}`, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!r.ok) return;
        setShareInboxPayload(await r.json());
        setScreen('shareInbox');
      } catch (err) { console.error('share-target redeem failed:', err); }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const goto = (s) => {
    setScreen(s);
    if (isMobile) window.scrollTo(0, 0);
    else document.getElementById('tp-main')?.scrollTo(0, 0);
  };

  const handleNav = (tab) => {
    setHomeTab(tab);
    if (screen !== 'home') goto('home');
  };

  const handleNewTrip = () => {
    setTripDraft({ destination: '', dateStart: '', dateEnd: '', travelers: 2, legs: [], primaryDays: undefined });
    setSelectedIdeas([]);
    setSelectedPresets([]);
    setBrainstormCustomIdeas([]);
    setCurrentTripId(null);
    goto('newtrip');
  };

  const handleOpenTrip = (id) => {
    const trip = trips.find(t => t.id === id);
    if (!trip) return;
    setCurrentTripId(id);
    setSelectedIdeas(reconcileSelectedIdeas(trip.selectedIdeas, ideasForTrip(trip)));
    setDayCount(trip.dayCount || 5);
    const legs = Array.isArray(trip.legs) ? trip.legs : null;
    setTripDraft({
      destination: (legs?.[0]?.destination) || trip.destination || '',
      dateStart: trip.dateStart || '',
      dateEnd: trip.dateEnd || '',
      travelers: trip.travelers || 2,
      legs: legs && legs.length > 1 ? legs.slice(1) : [],
      primaryDays: legs && legs.length > 1 ? legs[0]?.dayCount : undefined,
    });
    goto('itinerary');
  };

  const handleDeleteTrip = async (id) => {
    await apiDeleteTrip(user, id).catch(console.error);
    setTrips(prev => prev.filter(t => t.id !== id));
    if (currentTripId === id) {
      setCurrentTripId(null);
      goto('home');
    }
  };

  const handleTripDraftChange = (data) => {
    setTripDraft(prev => ({ ...prev, ...data }));
  };

  const handleChoosePath = async (path, entryData, reset = true) => {
    if (entryData) setTripDraft(prev => ({ ...prev, ...entryData }));
    if (path === 'scratch') {
      setSelectedIdeas([]);
      setSelectedPresets([]);
      await finishTripCreation([]);
      return;
    }
    if (reset) {
      const ideas = destIdeas;
      if (path === 'preset') {
        setSelectedIdeas([]);
        setSelectedPresets([]);
      } else {
        setSelectedIdeas(ideas.map(i => i.id));
      }
    }
    goto(path === 'preset' ? 'presets' : 'brainstorm');
  };

  const handleApplyPresets = async () => {
    const ideas = selectedIdeas.length > 0 ? selectedIdeas : destIdeas.map(i => i.id);
    await finishTripCreation(ideas);
  };

  const handleBrainstormContinue = async () => {
    await finishTripCreation(selectedIdeas);
  };

  const finishTripCreation = async (ideas) => {
    if (!user) return;
    const legsPayload = fullLegs;
    const title = legsPayload
      ? legsPayload.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ') || 'New Trip'
      : (tripDraft.destination?.split(',')[0] || 'New Trip');
    if (currentTripId) {
      const updates = {
        selectedIdeas: ideas, dayCount,
        destination: tripDraft.destination || '',
        legs: legsPayload,
        dateStart: tripDraft.dateStart || '',
        dateEnd: tripDraft.dateEnd || '',
        travelers: tripDraft.travelers || 2,
        title,
        customIdeas: brainstormCustomIdeas,
      };
      setTrips(prev => prev.map(t => t.id === currentTripId ? { ...t, ...updates } : t));
      await apiUpdateTrip(user, currentTripId, updates).catch(console.error);
      goto('itinerary');
      return;
    }
    const tripData = {
      destination: tripDraft.destination || 'Lisbon, Portugal',
      legs: legsPayload,
      dateStart: tripDraft.dateStart || '',
      dateEnd: tripDraft.dateEnd || '',
      travelers: tripDraft.travelers || 2,
      title,
      swatch: destSwatch(tripDraft.destination || ''),
      selectedIdeas: ideas,
      customIdeas: brainstormCustomIdeas,
      dayCount,
      bookings: {},
      status: 'planning',
    };
    const newId = await apiCreateTrip(user, tripData);
    const newTrip = { id: newId, ...tripData, createdAt: Date.now(), updatedAt: Date.now() };
    setTrips(prev => [newTrip, ...prev]);
    setCurrentTripId(newId);
    setSelectedIdeas(ideas);
    goto('itinerary');
  };

  const localUpdate = (id, updates) => {
    setTrips(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const handleItinerarySave = (ideas, days) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { selectedIdeas: ideas, dayCount: days });
    apiUpdateTrip(user, currentTripId, { selectedIdeas: ideas, dayCount: days }).catch(console.error);
  };

  const handleSaveCustomIdea = (idea) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const existing = Array.isArray(trip?.customIdeas) ? trip.customIdeas : Object.values(trip?.customIdeas || {});
    const customIdeas = [idea, ...existing.filter(i => i.id !== idea.id)];
    localUpdate(currentTripId, { customIdeas });
    apiUpdateTrip(user, currentTripId, { customIdeas }).catch(console.error);
  };

  const handleDeleteCustomIdea = (id) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const existing = Array.isArray(trip?.customIdeas) ? trip.customIdeas : Object.values(trip?.customIdeas || {});
    const customIdeas = existing.filter(i => i.id !== id);
    localUpdate(currentTripId, { customIdeas });
    apiUpdateTrip(user, currentTripId, { customIdeas }).catch(console.error);
  };

  const handleSaveOverrides = (ideaOverrides) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { ideaOverrides });
    apiUpdateTrip(user, currentTripId, { ideaOverrides }).catch(console.error);
  };

  const handleSaveTripDetails = (details) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const tripDetails = { ...(trip?.tripDetails || {}), ...details, savedAt: Date.now() };

    const updates = { tripDetails, ...mergeScannedTripDetails(trip, details) };

    localUpdate(currentTripId, updates);
    apiUpdateTrip(user, currentTripId, updates).catch(console.error);
  };

  // Share-inbox versions of handleSaveCustomIdea/handleSaveTripDetails,
  // parameterized by tripId instead of closing over currentTripId — the
  // share flow saves into whichever trip the user picks, which usually
  // isn't the trip currently open (there may be no trip open at all).
  // Both of these are called from ShareInboxScreen, including from a
  // fire-and-forget background update that resolves seconds after the user
  // has already been sent home (see ShareInboxScreen's "save immediately,
  // extract in the background" flow) — by then ShareInboxScreen has long
  // unmounted and this function's own `trips`/`tripId` closure is stale.
  // Computing the update inside the setTrips functional updater (against
  // `prev`, never the outer `trips`) is what makes that safe: React always
  // hands the updater the latest state, no matter how old the closure
  // calling it is.
  const handleShareAddIdea = (tripId, idea) => {
    setTrips(prev => prev.map(t => {
      if (t.id !== tripId) return t;
      const existingCustom = Array.isArray(t.customIdeas) ? t.customIdeas : Object.values(t.customIdeas || {});
      const customIdeas = [idea, ...existingCustom.filter(i => i.id !== idea.id)];
      const existingSelected = Array.isArray(t.selectedIdeas) ? t.selectedIdeas : [];
      const selectedIdeas = existingSelected.includes(idea.id) ? existingSelected : [...existingSelected, idea.id];
      apiUpdateTrip(user, tripId, { customIdeas, selectedIdeas }).catch(console.error);
      return { ...t, customIdeas, selectedIdeas };
    }));
  };

  const handleShareAddBooking = (tripId, details) => {
    setTrips(prev => prev.map(t => {
      if (t.id !== tripId) return t;
      const tripDetails = { ...(t.tripDetails || {}), ...details, savedAt: Date.now() };
      const updates = { tripDetails, ...mergeScannedTripDetails(t, details) };
      apiUpdateTrip(user, tripId, updates).catch(console.error);
      return { ...t, ...updates };
    }));
  };

  const handleSavePlan = (customPlan) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { customPlan }); // keep as array in memory
    // Firestore doesn't allow nested arrays — store as JSON string
    const serialized = customPlan != null ? JSON.stringify(customPlan) : null;
    apiUpdateTrip(user, currentTripId, { customPlan: serialized }).catch(console.error);
  };

  const handleBookingToggle = (itemId, checked) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const bookings = { ...(trip?.bookings || {}), [itemId]: checked };
    localUpdate(currentTripId, { bookings });
    apiUpdateTrip(user, currentTripId, { bookings }).catch(console.error);
  };

  const handleSaveBookingDetails = (itemId, details) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const bookings = { ...(trip?.bookings || {}), [`${itemId}_details`]: details };
    localUpdate(currentTripId, { bookings });
    apiUpdateTrip(user, currentTripId, { bookings }).catch(console.error);
  };

  const handleSaveDates = ({ dateStart, dateEnd, travelers }) => {
    if (!currentTripId) return;
    const updates = { dateStart, dateEnd, travelers };
    const trip = trips.find(t => t.id === currentTripId);
    const isMultiCity = trip?.legs && trip.legs.length > 1;
    // Multi-city trips: total length is driven by the cities' day counts, so
    // dateEnd here is whatever DatesPanel already computed from them (via
    // setMultiDateStart) — don't re-derive dayCount from the date diff and
    // clobber it; city edits update dayCount through handleSaveLegs instead.
    if (!isMultiCity && dateStart && dateEnd) {
      const d1 = new Date(dateStart + 'T12:00:00');
      const d2 = new Date(dateEnd + 'T12:00:00');
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
      if (diff > 0 && diff <= 60) { updates.dayCount = diff; setDayCount(diff); }
    }
    setTripDraft(prev => ({ ...prev, dateStart, dateEnd, travelers }));
    localUpdate(currentTripId, updates);
    apiUpdateTrip(user, currentTripId, updates).catch(console.error);
  };

  // Multi-city trips: DatesPanel's route builder edits legs directly (city
  // add/remove/rename/resize). dayCount and dateEnd are re-derived from the
  // new legs so they never disagree with what the cities add up to.
  const handleSaveLegs = (legs) => {
    if (!currentTripId) return;
    const trip = trips.find(t => t.id === currentTripId);
    const dayCount = computeLegSchedule(legs).totalDays;
    const title = legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ') || 'New Trip';
    const updates = {
      legs,
      destination: legs[0]?.destination || '',
      title,
      dayCount,
      dateEnd: trip?.dateStart ? addDaysIso(trip.dateStart, dayCount - 1) : (trip?.dateEnd || ''),
    };
    setDayCount(dayCount);
    // tripDraft.legs/primaryDays must stay in sync with what was just saved —
    // otherwise the fullLegs-driven dayCount effect below recomputes from a
    // STALE tripDraft snapshot (from whenever the trip was opened) on the
    // very next render and silently reverts the dayCount we just set here,
    // flapping it back and forth on every leg/date edit.
    setTripDraft(prev => ({
      ...prev,
      destination: updates.destination,
      dateEnd: updates.dateEnd,
      legs: legs.length > 1 ? legs.slice(1) : [],
      primaryDays: legs.length > 1 ? legs[0]?.dayCount : undefined,
    }));
    localUpdate(currentTripId, updates);
    apiUpdateTrip(user, currentTripId, updates).catch(console.error);
  };

  const handleSaveAccommodation = (accommodations) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { accommodations });
    apiUpdateTrip(user, currentTripId, { accommodations }).catch(console.error);
  };

  const handleSaveDayNotes = (dayNotes) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { dayNotes });
    apiUpdateTrip(user, currentTripId, { dayNotes }).catch(console.error);
  };

  const handleSavePacking = (packingList) => {
    if (!currentTripId) return;
    localUpdate(currentTripId, { packingList });
    apiUpdateTrip(user, currentTripId, { packingList }).catch(console.error);
  };

  const handleShare = async () => {
    if (!currentTripId || !user) return null;
    const token = await getToken(user);
    const r = await fetch(`/api/tp/trips/${currentTripId}/share`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error('share failed');
    const { token: shareToken } = await r.json();
    return `${window.location.origin}${window.location.pathname}?share=${shareToken}`;
  };

  const currentTrip = trips.find(t => t.id === currentTripId) || null;

  // Shared itinerary view — public, no auth required
  const shareParam = new URLSearchParams(window.location.search).get('share');
  if (shareParam) return <SharedItineraryView token={shareParam} />;

  // Auth loading
  if (authLoading) {
    return (
      <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkMute }}>Loading…</div>
      </div>
    );
  }

  // Not signed in — show login
  if (!user) return <LoginScreen />;

  const renderContent = () => {
    switch (screen) {
      case 'home':
        return (
          <HomeScreen
            trips={trips}
            isMobile={isMobile}
            activeTab={homeTab}
            onTabChange={handleNav}
            onNewTrip={handleNewTrip}
            onOpenTrip={handleOpenTrip}
            onDeleteTrip={handleDeleteTrip}
          />
        );
      case 'newtrip':
        return (
          <NewTripScreen
            isMobile={isMobile}
            draft={tripDraft}
            onDraftChange={handleTripDraftChange}
            onBack={() => currentTripId ? goto('itinerary') : goto('home')}
            onContinue={(data) => {
              if (data) setTripDraft(prev => ({ ...prev, ...data }));
              goto('planningpath');
            }}
          />
        );
      case 'planningpath':
        return (
          <PlanningPathScreen
            isMobile={isMobile}
            destination={tripDraft.destination}
            onBack={() => goto('newtrip')}
            onChoose={(path) => handleChoosePath(path, tripDraft)}
          />
        );
      case 'presets':
        return (
          <PresetsScreen
            isMobile={isMobile}
            destination={tripDraft.destination}
            selectedPresets={selectedPresets}
            setSelectedPresets={setSelectedPresets}
            selectedIdeas={selectedIdeas}
            setSelectedIdeas={setSelectedIdeas}
            onBack={() => currentTripId ? goto('itinerary') : goto('planningpath')}
            onContinue={handleApplyPresets}
            destIdeas={destIdeas}
          />
        );
      case 'brainstorm':
        return (
          <BrainstormScreen
            isMobile={isMobile}
            destination={tripDraft.destination}
            selectedIdeas={selectedIdeas}
            setSelectedIdeas={setSelectedIdeas}
            onBack={() => currentTripId ? goto('itinerary') : goto('planningpath')}
            onContinue={handleBrainstormContinue}
            destIdeas={destIdeas}
            customIdeas={brainstormCustomIdeas}
            onAddCustom={(idea) => {
              setBrainstormCustomIdeas(prev => [idea, ...prev]);
              setSelectedIdeas(prev => prev.includes(idea.id) ? prev : [idea.id, ...prev]);
            }}
          />
        );
      case 'itinerary':
        return (
          <ItineraryScreen
            isMobile={isMobile}
            trip={currentTrip}
            selectedIds={selectedIdeas}
            setSelectedIds={(ids) => {
              setSelectedIdeas(ids);
              handleItinerarySave(ids, dayCount);
            }}
            dayCount={dayCount}
            setDayCount={(d) => {
              setDayCount(d);
              handleItinerarySave(selectedIdeas, d);
            }}
            onBack={() => goto('home')}
            onBook={() => handleItinerarySave(selectedIdeas, dayCount)}
            onDelete={() => handleDeleteTrip(currentTripId)}
            onSavePlan={handleSavePlan}
            onSaveCustomIdea={handleSaveCustomIdea}
            onDeleteCustomIdea={handleDeleteCustomIdea}
            onSaveOverrides={handleSaveOverrides}
            onSaveTripDetails={handleSaveTripDetails}
            bookings={currentTrip?.bookings || {}}
            destIdeas={destIdeas}
            onToggleBooked={handleBookingToggle}
            onSaveDetails={handleSaveBookingDetails}
            onSavePacking={handleSavePacking}
            selectedPresets={selectedPresets}
            setSelectedPresets={setSelectedPresets}
            onSaveDates={handleSaveDates}
            onSaveLegs={handleSaveLegs}
            onSaveAccommodation={handleSaveAccommodation}
            onSaveDayNotes={handleSaveDayNotes}
            onShare={handleShare}
          />
        );
      case 'shareInbox':
        return (
          <ShareInboxScreen
            isMobile={isMobile}
            payload={shareInboxPayload}
            trips={trips}
            onAddIdea={handleShareAddIdea}
            onAddBooking={handleShareAddBooking}
            onNewTrip={handleNewTrip}
            onDone={() => goto('home')}
          />
        );
      case 'booking':
        return (
          <BookingScreen
            isMobile={isMobile}
            trip={currentTrip}
            onBack={() => goto('itinerary')}
            onDone={() => goto('home')}
            onToggleBooked={handleBookingToggle}
            onSaveDetails={handleSaveBookingDetails}
            onSavePacking={handleSavePacking}
          />
        );
      default:
        return (
          <HomeScreen
            trips={trips}
            isMobile={isMobile}
            activeTab={homeTab}
            onTabChange={handleNav}
            onNewTrip={handleNewTrip}
            onOpenTrip={handleOpenTrip}
            onDeleteTrip={handleDeleteTrip}
          />
        );
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: isMobile ? PALETTE.paper : PALETTE.paperDim }}>
      {!isMobile && (
        <Sidebar
          screen={screen}
          homeTab={homeTab}
          onNav={handleNav}
          trips={trips}
          onOpenTrip={handleOpenTrip}
          onNewTrip={handleNewTrip}
          user={user}
          onLogout={logout}
        />
      )}
      <main
        id="tp-main"
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: isMobile ? undefined : 'auto',
          height: isMobile ? undefined : '100vh',
          background: PALETTE.paper,
        }}
      >
        {renderContent()}
      </main>
      <VersionBadge />
    </div>
  );
}
